import { useEffect, useMemo, useState } from "react";
import moment from "moment";
import {
  Broadcast,
  BroadcastBody,
  createBroadcast,
  deleteBroadcast,
  isBroadcastLive,
  listBroadcasts,
  updateBroadcast,
} from "../../../api/broadcast.api";

const C = {
  espresso: "#5B3A29",
  caramel:  "#D97706",
  emerald:  "#059669",
  rose:     "#E11D48",
  amber:    "#F59E0B",
  sky:      "#0EA5E9",
};
const HERO_GRAD = "linear-gradient(135deg,#FFFBEB 0%,#FEE2C7 45%,#FECACA 100%)";

type FormState = {
  id: number | null; // null = creating
  text: string;
  startAt: string; // ISO local format compatible with datetime-local
  endAt: string;
};

const EMPTY_FORM: FormState = { id: null, text: "", startAt: "", endAt: "" };

function toLocalInput(iso: string | null): string {
  return iso ? moment(iso).format("YYYY-MM-DDTHH:mm") : "";
}
function toIso(local: string): string | null {
  return local ? new Date(local).toISOString() : null;
}

function broadcastState(b: Broadcast, now: Date): "live" | "upcoming" | "ended" {
  if (isBroadcastLive(b, now)) return "live";
  const start = b.startAt ? new Date(b.startAt).getTime() : -Infinity;
  return start > now.getTime() ? "upcoming" : "ended";
}

function fmtWindow(b: Broadcast): string {
  if (!b.startAt && !b.endAt) return "Always visible";
  const s = b.startAt ? moment(b.startAt).format("D MMM YYYY HH:mm") : "—";
  const e = b.endAt   ? moment(b.endAt).format("D MMM YYYY HH:mm")   : "—";
  return `${s} → ${e}`;
}

const BroadcastsPage: React.FC = () => {
  const [list, setList] = useState<Broadcast[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(t);
  }, []);

  const refresh = async () => {
    setLoading(true);
    try {
      const rows = await listBroadcasts();
      // Sort: live first, then upcoming (earliest first), then ended (most recent first).
      rows.sort((a, b) => {
        const aState = broadcastState(a, new Date());
        const bState = broadcastState(b, new Date());
        const order = { live: 0, upcoming: 1, ended: 2 };
        if (aState !== bState) return order[aState] - order[bState];
        if (aState === "upcoming") {
          return (a.startAt ? new Date(a.startAt).getTime() : 0) - (b.startAt ? new Date(b.startAt).getTime() : 0);
        }
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      });
      setList(rows);
    } catch (e) {
      console.error(e);
      setError("Could not load broadcasts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const stats = useMemo(() => {
    const live = list.filter((b) => broadcastState(b, now) === "live").length;
    const upcoming = list.filter((b) => broadcastState(b, now) === "upcoming").length;
    const ended = list.filter((b) => broadcastState(b, now) === "ended").length;
    return { total: list.length, live, upcoming, ended };
  }, [list, now]);

  const handleEdit = (b: Broadcast) => {
    setForm({
      id: b.id,
      text: b.text,
      startAt: toLocalInput(b.startAt),
      endAt: toLocalInput(b.endAt),
    });
    setError(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCancel = () => {
    setForm(EMPTY_FORM);
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.text.trim()) {
      setError("Please enter the broadcast text");
      return;
    }
    if (form.startAt && form.endAt && new Date(form.startAt) > new Date(form.endAt)) {
      setError("Start must be before end");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const body: BroadcastBody = {
        text: form.text.trim(),
        startAt: toIso(form.startAt),
        endAt: toIso(form.endAt),
      };
      if (form.id == null) {
        await createBroadcast(body);
      } else {
        await updateBroadcast(form.id, body);
      }
      setForm(EMPTY_FORM);
      await refresh();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (b: Broadcast) => {
    if (!window.confirm(`Delete broadcast?\n\n"${b.text}"`)) return;
    try {
      await deleteBroadcast(b.id);
      if (form.id === b.id) setForm(EMPTY_FORM);
      await refresh();
    } catch (e) {
      console.error(e);
      setError("Delete failed");
    }
  };

  const isEdit = form.id != null;

  return (
    <div className="min-h-screen font-sans pb-[60px]" style={{ background: "#FAFAF7" }}>
      <header className="px-5 pt-[24px] pb-[20px]">
        <div
          className="rounded-3xl p-5 md:p-6 relative overflow-hidden"
          style={{ background: HERO_GRAD, boxShadow: "0 12px 36px -16px rgba(217,119,6,0.25)" }}
        >
          <div
            aria-hidden
            className="absolute -right-12 -top-12 w-[180px] h-[180px] rounded-full opacity-25"
            style={{ background: "radial-gradient(circle,#92400E 0%,transparent 70%)" }}
          />
          <p className="text-[11px] font-bold tracking-[0.12em] uppercase relative" style={{ color: C.espresso }}>
            📣 Broadcasts
          </p>
          <h1
            className="font-display font-black mt-2 leading-[0.95] tracking-tightest relative"
            style={{ color: "#1B0F08", fontSize: "clamp(32px,7vw,48px)" }}
          >
            {stats.total} {stats.total === 1 ? "message" : "messages"}
          </h1>
          <div className="mt-4 flex gap-2 flex-wrap relative">
            <Chip color={C.emerald} label={`${stats.live} live`}     dot />
            <Chip color={C.amber}   label={`${stats.upcoming} upcoming`} />
            <Chip color="#A38B6A"   label={`${stats.ended} ended`} />
          </div>
        </div>
      </header>

      <main className="px-5 space-y-5">

        {/* ---------- Form ---------- */}
        <section>
          <Eyebrow icon={isEdit ? "✏️" : "✚"} color={C.caramel}>
            {isEdit ? `Editing #${form.id}` : "New broadcast"}
          </Eyebrow>
          <form
            onSubmit={handleSubmit}
            className="bg-white rounded-2xl p-4 mt-2 space-y-3"
            style={{ boxShadow: "0 1px 0 #ECE5D5,0 4px 24px -16px rgba(91,58,41,0.18)" }}
          >
            <div>
              <label className="block text-[11px] font-extrabold uppercase tracking-[0.10em] mb-1.5" style={{ color: C.espresso }}>
                Message
              </label>
              <textarea
                value={form.text}
                onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))}
                placeholder="e.g. We're closing early today at 4 PM ☕"
                rows={3}
                className="w-full px-3 py-2 rounded-xl text-sm focus:outline-none resize-none"
                style={{ background: "#FAFAF7", border: "2px solid #E7DFD2", color: "#1B0F08" }}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-extrabold uppercase tracking-[0.10em] mb-1.5" style={{ color: C.espresso }}>
                  Start (optional)
                </label>
                <input
                  type="datetime-local"
                  value={form.startAt}
                  onChange={(e) => setForm((f) => ({ ...f, startAt: e.target.value }))}
                  className="w-full h-10 px-3 rounded-xl text-sm focus:outline-none"
                  style={{ background: "#FAFAF7", border: "2px solid #E7DFD2", color: "#1B0F08" }}
                />
              </div>
              <div>
                <label className="block text-[11px] font-extrabold uppercase tracking-[0.10em] mb-1.5" style={{ color: C.espresso }}>
                  End (optional)
                </label>
                <input
                  type="datetime-local"
                  value={form.endAt}
                  onChange={(e) => setForm((f) => ({ ...f, endAt: e.target.value }))}
                  className="w-full h-10 px-3 rounded-xl text-sm focus:outline-none"
                  style={{ background: "#FAFAF7", border: "2px solid #E7DFD2", color: "#1B0F08" }}
                />
              </div>
            </div>
            <p className="text-[11px] font-semibold" style={{ color: "#A38B6A" }}>
              Leave both empty for an always-visible broadcast.
            </p>
            {error && (
              <p className="text-sm font-bold px-3 py-2 rounded-lg" style={{ background: "#FEE2E2", color: C.rose }}>
                {error}
              </p>
            )}
            <div className="flex gap-2 flex-wrap pt-1">
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center justify-center h-10 px-5 rounded-full text-sm font-bold text-white disabled:opacity-40 active:scale-[0.97] transition-all"
                style={{ background: C.espresso }}
              >
                {saving ? "Saving…" : isEdit ? "Update broadcast" : "Publish broadcast"}
              </button>
              {isEdit && (
                <button
                  type="button"
                  onClick={handleCancel}
                  className="inline-flex items-center justify-center h-10 px-5 rounded-full text-sm font-bold transition-all"
                  style={{ background: "#F3EBDA", color: C.espresso }}
                >
                  Cancel edit
                </button>
              )}
            </div>
          </form>
        </section>

        {/* ---------- List ---------- */}
        <section>
          <Eyebrow icon="📋" color={C.sky}>All broadcasts</Eyebrow>
          {loading ? (
            <div className="bg-white rounded-2xl p-8 mt-2 flex items-center justify-center" style={{ boxShadow: "0 1px 0 #ECE5D5" }}>
              <svg className="w-6 h-6 animate-spin" fill="none" viewBox="0 0 24 24" style={{ color: C.caramel }}>
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.25"/>
                <path d="M12 2a10 10 0 0110 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
              </svg>
            </div>
          ) : list.length === 0 ? (
            <div className="bg-white rounded-2xl p-8 mt-2 text-center" style={{ boxShadow: "0 1px 0 #ECE5D5" }}>
              <div className="text-4xl mb-2">📭</div>
              <p className="font-display font-black text-base" style={{ color: "#1B0F08" }}>No broadcasts yet</p>
              <p className="text-sm mt-1" style={{ color: "#A38B6A" }}>Create one above to announce something.</p>
            </div>
          ) : (
            <div className="space-y-3 mt-2">
              {list.map((b) => {
                const state = broadcastState(b, now);
                const stateMeta =
                  state === "live"     ? { label: "Live",     color: C.emerald, bg: "#D1FAE5" }
                  : state === "upcoming" ? { label: "Upcoming", color: C.amber,   bg: "#FEF3C7" }
                  : { label: "Ended",    color: "#A38B6A", bg: "#F3EBDA" };
                const editing = form.id === b.id;
                return (
                  <div
                    key={b.id}
                    className="bg-white rounded-2xl p-4 relative overflow-hidden"
                    style={{
                      boxShadow: editing
                        ? `0 0 0 2px ${C.caramel}, 0 4px 24px -16px rgba(91,58,41,0.18)`
                        : "0 1px 0 #ECE5D5,0 4px 24px -16px rgba(91,58,41,0.18)",
                    }}
                  >
                    {state === "live" && (
                      <div className="absolute top-0 left-0 right-0 h-[3px] rounded-t-2xl" style={{ background: C.emerald }} />
                    )}
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span
                            className="inline-flex items-center gap-1.5 h-[22px] px-2.5 rounded-full text-[10px] font-extrabold uppercase tracking-wider"
                            style={{ background: stateMeta.bg, color: stateMeta.color }}
                          >
                            {state === "live" && <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: stateMeta.color }} />}
                            {stateMeta.label}
                          </span>
                          <span className="text-[11px] font-semibold" style={{ color: "#A38B6A" }}>
                            #{b.id} · created {moment(b.createdAt).fromNow()}
                          </span>
                        </div>
                        <p
                          className="mt-2 font-display font-black whitespace-pre-wrap break-words"
                          style={{ color: "#1B0F08", fontSize: "16px", lineHeight: 1.35 }}
                        >
                          {b.text}
                        </p>
                        <p className="mt-2 text-xs font-semibold flex items-center gap-1.5" style={{ color: "#A38B6A" }}>
                          <span>🗓</span> {fmtWindow(b)}
                        </p>
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        <button
                          type="button"
                          onClick={() => handleEdit(b)}
                          className="inline-flex items-center justify-center h-9 px-4 rounded-full text-xs font-bold transition-all"
                          style={{ background: "#F3EBDA", color: C.espresso }}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(b)}
                          className="inline-flex items-center justify-center h-9 px-4 rounded-full text-xs font-bold transition-all"
                          style={{ background: "#FEE2E2", color: C.rose }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

// ----------------------------- subcomponents -----------------------------

const Eyebrow: React.FC<{ icon: string; color: string; children: React.ReactNode }> = ({ icon, color, children }) => (
  <p className="text-[11px] font-extrabold tracking-[0.10em] uppercase flex items-center gap-1.5" style={{ color }}>
    <span className="text-[14px] leading-none">{icon}</span>
    {children}
  </p>
);

const Chip: React.FC<{ color: string; label: string; dot?: boolean }> = ({ color, label, dot }) => (
  <span
    className="inline-flex items-center gap-1.5 h-[26px] px-3 rounded-full text-[11px] font-extrabold uppercase tracking-wider"
    style={{ background: "rgba(255,255,255,0.7)", color }}
  >
    {dot && <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: color }} />}
    {label}
  </span>
);

export default BroadcastsPage;
